import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, PencilIcon, Trash2Icon, ArrowUpIcon, ArrowDownIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Client, SortConfig } from '../types/catalog';
import { generateMockClients } from '../utils/catalogData';
export function ClientsCatalog() {
  // Data State
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  // Filter & Sort State
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'createdAt',
    direction: 'desc'
  });
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Selection State
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  // Form State
  const [formData, setFormData] = useState({
    clientCode: '',
    clientName: '',
    description: '',
    status: 'active' as 'active' | 'inactive'
  });
  const [formErrors, setFormErrors] = useState({
    clientCode: false,
    clientName: false
  });
  // Delete Confirm State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  // Load initial data
  useEffect(() => {
    setClients(generateMockClients());
    setIsLoaded(true);
  }, []);
  // Derived Data (Filtering & Sorting)
  const filteredAndSortedClients = useMemo(() => {
    let result = [...clients];
    // Apply Search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(client => client.clientCode.toLowerCase().includes(searchLower) || client.clientName.toLowerCase().includes(searchLower) || client.description.toLowerCase().includes(searchLower));
    }
    // Apply Status Filter
    if (statusFilter !== 'ALL') {
      result = result.filter(client => client.status === statusFilter.toLowerCase());
    }
    // Apply Sorting
    result.sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Client];
      const bValue = b[sortConfig.key as keyof Client];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [clients, search, statusFilter, sortConfig]);
  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedClients.length / pageSize);
  const paginatedClients = filteredAndSortedClients.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, pageSize]);
  // Handlers
  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(new Set(paginatedClients.map(c => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const handleBulkAction = (newStatus: 'active' | 'inactive') => {
    setClients(prev => prev.map(client => selectedIds.has(client.id) ? {
      ...client,
      status: newStatus
    } : client));
    setSelectedIds(new Set());
  };
  const openAddModal = () => {
    setEditingClient(null);
    setFormData({
      clientCode: '',
      clientName: '',
      description: '',
      status: 'active'
    });
    setFormErrors({
      clientCode: false,
      clientName: false
    });
    setIsModalOpen(true);
  };
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      clientCode: client.clientCode,
      clientName: client.clientName,
      description: client.description,
      status: client.status
    });
    setFormErrors({
      clientCode: false,
      clientName: false
    });
    setIsModalOpen(true);
  };
  const handleSaveClient = () => {
    // Validate
    const errors = {
      clientCode: !formData.clientCode.trim(),
      clientName: !formData.clientName.trim()
    };
    setFormErrors(errors);
    if (errors.clientCode || errors.clientName) return;
    if (editingClient) {
      // Update
      setClients(prev => prev.map(c => c.id === editingClient.id ? {
        ...c,
        ...formData
      } : c));
    } else {
      // Add new
      const newClient: Client = {
        id: `client-${Date.now()}`,
        ...formData,
        createdAt: new Date().toISOString()
      };
      setClients(prev => [newClient, ...prev]);
    }
    setIsModalOpen(false);
  };
  const confirmDelete = (client: Client) => {
    setClientToDelete(client);
    setIsConfirmOpen(true);
  };
  const handleDelete = () => {
    if (clientToDelete) {
      setClients(prev => prev.filter(c => c.id !== clientToDelete.id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(clientToDelete.id);
        return next;
      });
    }
    setIsConfirmOpen(false);
    setClientToDelete(null);
  };
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: string;
  }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1" data-id="element-175" /> : <ArrowDownIcon className="w-3 h-3 ml-1" data-id="element-176" />;
  };
  if (!isLoaded) return null;
  return <div className="space-y-6" data-id="element-177">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-178">
        <div data-id="element-179">
          <h2 className="text-2xl font-bold tracking-tight" data-id="element-180">
            Catálogo de Clientes
          </h2>
          <p className="text-muted-foreground mt-1" data-id="element-181">
            Administra las configuraciones y accesos de clientes
          </p>
        </div>
        <Button onClick={openAddModal} data-id="element-182">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-183" />
          Agregar Cliente
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-184">
        <div className="flex flex-col sm:flex-row gap-4 flex-1" data-id="element-185">
          <div className="relative w-full sm:w-72" data-id="element-186">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-187" />
            <Input placeholder="Buscar clientes..." className="pl-9 bg-background" value={search} onChange={e => setSearch(e.target.value)} data-id="element-188" />
          </div>
          <Select className="w-full sm:w-48 bg-background" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-189">
            <option value="ALL" data-id="element-190">Todos los Estados</option>
            <option value="ACTIVE" data-id="element-191">Activo</option>
            <option value="INACTIVE" data-id="element-192">Inactivo</option>
          </Select>
        </div>

        {selectedIds.size > 0 && <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-4" data-id="element-193">
            <span className="text-sm text-muted-foreground mr-2" data-id="element-194">
              {selectedIds.size} seleccionados
            </span>
            <Button variant="outline" size="sm" onClick={() => handleBulkAction('active')} className="text-green-500 hover:text-green-600 hover:bg-green-500/10 border-green-500/20" data-id="element-195">
              Activar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleBulkAction('inactive')} className="text-muted-foreground" data-id="element-196">
              Desactivar
            </Button>
          </div>}
      </div>

      {/* Table */}
      <div className="rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm" data-id="element-197">
        <Table data-id="element-198">
          <TableHeader className="bg-gray-50" data-id="element-199">
            <TableRow data-id="element-200">
              <TableHead className="w-[50px] text-center" data-id="element-201">
                <input type="checkbox" className="rounded border-input bg-background text-primary focus:ring-primary" checked={paginatedClients.length > 0 && selectedIds.size === paginatedClients.length} onChange={handleSelectAll} data-id="element-202" />
              </TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors" onClick={() => handleSort('clientCode')} data-id="element-203">
                <div className="flex items-center" data-id="element-204">
                  Código <SortIcon columnKey="clientCode" data-id="element-205" />
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors" onClick={() => handleSort('clientName')} data-id="element-206">
                <div className="flex items-center" data-id="element-207">
                  Nombre <SortIcon columnKey="clientName" data-id="element-208" />
                </div>
              </TableHead>
              <TableHead data-id="element-209">Descripción</TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors" onClick={() => handleSort('status')} data-id="element-210">
                <div className="flex items-center" data-id="element-211">
                  Estado <SortIcon columnKey="status" data-id="element-212" />
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors" onClick={() => handleSort('createdAt')} data-id="element-213">
                <div className="flex items-center" data-id="element-214">
                  Fecha de Creación <SortIcon columnKey="createdAt" data-id="element-215" />
                </div>
              </TableHead>
              <TableHead className="text-right" data-id="element-216">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-217">
            {paginatedClients.length === 0 ? <TableRow data-id="element-218">
                <TableCell colSpan={7} className="h-32 text-center text-muted-foreground" data-id="element-219">
                  No se encontraron clientes con los criterios indicados.
                </TableCell>
              </TableRow> : paginatedClients.map(client => <TableRow key={client.id} className={selectedIds.has(client.id) ? 'bg-muted/50' : ''} data-id="element-220">
                  <TableCell className="text-center" data-id="element-221">
                    <input type="checkbox" className="rounded border-input bg-background text-primary focus:ring-primary" checked={selectedIds.has(client.id)} onChange={() => handleSelectOne(client.id)} data-id="element-222" />
                  </TableCell>
                  <TableCell data-id="element-223">
                    <Badge variant="outline" className="font-mono bg-background" data-id="element-224">
                      {client.clientCode}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-foreground" data-id="element-225">
                    {client.clientName}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[200px] truncate" title={client.description} data-id="element-226">
                    {client.description || '-'}
                  </TableCell>
                  <TableCell data-id="element-227">
                    {client.status === 'active' ? <Badge variant="success" data-id="element-228">Activo</Badge> : <Badge variant="secondary" data-id="element-229">Inactivo</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" data-id="element-230">
                    {formatDate(client.createdAt)}
                  </TableCell>
                  <TableCell className="text-right" data-id="element-231">
                    <div className="flex justify-end space-x-1" data-id="element-232">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(client)} title="Editar" data-id="element-233">
                        <PencilIcon className="w-4 h-4 text-muted-foreground hover:text-foreground" data-id="element-234" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => confirmDelete(client)} title="Eliminar" data-id="element-235">
                        <Trash2Icon className="w-4 h-4 text-destructive/70 hover:text-destructive" data-id="element-236" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground" data-id="element-237">
        <div className="flex items-center space-x-2" data-id="element-238">
          <span data-id="element-239">Mostrar</span>
          <Select className="w-20 h-8" value={pageSize.toString()} onChange={e => setPageSize(Number(e.target.value))} data-id="element-240">
            <option value="10" data-id="element-241">10</option>
            <option value="25" data-id="element-242">25</option>
            <option value="50" data-id="element-243">50</option>
          </Select>
          <span data-id="element-244">por página</span>
        </div>

        <div data-id="element-245">
          Mostrando{' '}
          {filteredAndSortedClients.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}{' '}
          a {Math.min(currentPage * pageSize, filteredAndSortedClients.length)}{' '}
          de {filteredAndSortedClients.length} resultados
        </div>

        <div className="flex items-center space-x-2" data-id="element-246">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-247">
            Anterior
          </Button>
          <span className="px-2" data-id="element-248">
            Página {currentPage} de {Math.max(1, totalPages)}
          </span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} data-id="element-249">
            Siguiente
          </Button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingClient ? 'Editar Cliente' : 'Agregar Nuevo Cliente'} description={editingClient ? 'Actualiza los detalles de configuración del cliente.' : 'Crea un nuevo registro de cliente en el catálogo.'} data-id="element-250">
        <div className="space-y-4" data-id="element-251">
          <div className="grid grid-cols-2 gap-4" data-id="element-252">
            <div className="space-y-2" data-id="element-253">
              <label className="text-sm font-medium" data-id="element-254">
                Código de Cliente <span className="text-destructive" data-id="element-255">*</span>
              </label>
              <Input placeholder="ej. CL-001" value={formData.clientCode} onChange={e => setFormData(prev => ({
              ...prev,
              clientCode: e.target.value
            }))} className={formErrors.clientCode ? 'border-destructive' : ''} data-id="element-256" />
              {formErrors.clientCode && <p className="text-xs text-destructive" data-id="element-257">
                  El código de cliente es requerido
                </p>}
            </div>
            <div className="space-y-2" data-id="element-258">
              <label className="text-sm font-medium" data-id="element-259">Estado</label>
              <div className="flex p-1 bg-muted rounded-md" data-id="element-260">
                <button className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${formData.status === 'active' ? 'bg-background shadow-sm text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => setFormData(prev => ({
                ...prev,
                status: 'active'
              }))} data-id="element-261">
                  Activo
                </button>
                <button className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${formData.status === 'inactive' ? 'bg-background shadow-sm text-foreground font-medium' : 'text-muted-foreground hover:text-foreground'}`} onClick={() => setFormData(prev => ({
                ...prev,
                status: 'inactive'
              }))} data-id="element-262">
                  Inactivo
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-2" data-id="element-263">
            <label className="text-sm font-medium" data-id="element-264">
              Nombre del Cliente <span className="text-destructive" data-id="element-265">*</span>
            </label>
            <Input placeholder="ej. Acme Corporation" value={formData.clientName} onChange={e => setFormData(prev => ({
            ...prev,
            clientName: e.target.value
          }))} className={formErrors.clientName ? 'border-destructive' : ''} data-id="element-266" />
            {formErrors.clientName && <p className="text-xs text-destructive" data-id="element-267">
                El nombre del cliente es requerido
              </p>}
          </div>

          <div className="space-y-2" data-id="element-268">
            <label className="text-sm font-medium" data-id="element-269">Descripción</label>
            <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="Breve descripción del cliente..." value={formData.description} onChange={e => setFormData(prev => ({
            ...prev,
            description: e.target.value
          }))} data-id="element-270" />
          </div>

          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-border/50" data-id="element-271">
            <Button variant="outline" onClick={() => setIsModalOpen(false)} data-id="element-272">
              Cancelar
            </Button>
            <Button onClick={handleSaveClient} data-id="element-273">Guardar Cliente</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Cliente" message={`¿Estás seguro de que deseas eliminar a ${clientToDelete?.clientName}? Esta acción no se puede deshacer y puede afectar los logs asociados.`} confirmText="Eliminar Cliente" cancelText="Cancelar" data-id="element-274" />
    </div>;
}